// WAV 格式常量
const WAV_CONSTANTS = {
  HEADER_SIZE: 44,
  RIFF_HEADER_SIZE: 12,
  CHUNK_HEADER_SIZE: 8,
  SAMPLE_RATE: 44100,
  CHANNELS: 2,
  BITS_PER_SAMPLE: 16,
  BYTES_PER_SAMPLE: 2,
  MAX_SAMPLE_VALUE: 0x7fff,
  MAX_FILE_SIZE: 100 * 1024 * 1024, // 100MB 限制
} as const;

interface AudioFileInfo {
  buffer: AudioBuffer | null;
  arrayBuffer: ArrayBuffer;
  isValid: boolean;
  error?: string;
}

interface TargetAudioFormat {
  sampleRate: number;
  numberOfChannels: number;
}

// 全局 AudioContext 实例管理
let audioContextInstance: AudioContext | null = null;

async function getAudioContext(): Promise<AudioContext> {
  if (!audioContextInstance) {
    const AudioContextConstructor =
      window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
    audioContextInstance = new AudioContextConstructor();
  }

  // 检查和恢复 suspended 状态（自动播放政策）
  if (audioContextInstance.state === 'suspended') {
    try {
      await audioContextInstance.resume();
    } catch (error) {
      console.warn('Failed to resume AudioContext:', error);
    }
  }

  return audioContextInstance;
}

async function closeAudioContext(): Promise<void> {
  if (audioContextInstance) {
    try {
      await audioContextInstance.close();
    } catch (error) {
      console.warn('Failed to close AudioContext:', error);
    } finally {
      audioContextInstance = null;
    }
  }
}

/**
 * 验证浏览器兼容性
 */
function checkBrowserSupport(): { supported: boolean; error?: string } {
  if (
    typeof AudioContext === 'undefined' &&
    typeof (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext === 'undefined'
  ) {
    return { supported: false, error: 'AudioContext not supported in this browser' };
  }

  if (typeof DataView === 'undefined') {
    return { supported: false, error: 'DataView not supported in this browser' };
  }

  return { supported: true };
}

/**
 * 验证文件大小和类型
 */
function validateFile(file: File): { valid: boolean; error?: string } {
  if (file.size > WAV_CONSTANTS.MAX_FILE_SIZE) {
    return { valid: false, error: `File size exceeds ${WAV_CONSTANTS.MAX_FILE_SIZE / 1024 / 1024}MB limit` };
  }

  if (!file.type.includes('audio') && !file.name.toLowerCase().endsWith('.wav')) {
    return { valid: false, error: 'File must be an audio file' };
  }

  return { valid: true };
}

/**
 * 优化的字符串读取函数
 */
function readChunkId(view: DataView, offset: number): string {
  const chars = new Uint8Array(view.buffer, view.byteOffset + offset, 4);
  return String.fromCharCode(chars[0], chars[1], chars[2], chars[3]);
}

/**
 * 验证 WAV 文件格式（简化版，主要验证用于 chunk 解析）
 */
function isValidWavFile(buffer: ArrayBuffer): boolean {
  if (buffer.byteLength < WAV_CONSTANTS.RIFF_HEADER_SIZE) return false;

  const view = new DataView(buffer);
  const riffHeader = readChunkId(view, 0);
  const waveHeader = readChunkId(view, 8);

  return riffHeader === 'RIFF' && waveHeader === 'WAVE';
}

function isSameAudioFormat(buffer: AudioBuffer, target: TargetAudioFormat): boolean {
  return buffer.sampleRate === target.sampleRate && buffer.numberOfChannels === target.numberOfChannels;
}

async function normalizeAudioBuffer(buffer: AudioBuffer, target: TargetAudioFormat): Promise<AudioBuffer> {
  if (isSameAudioFormat(buffer, target)) {
    return buffer;
  }

  const frameCount = Math.max(1, Math.ceil(buffer.duration * target.sampleRate));
  const offlineContext = new OfflineAudioContext(target.numberOfChannels, frameCount, target.sampleRate);
  const source = offlineContext.createBufferSource();
  source.buffer = buffer;
  source.connect(offlineContext.destination);
  source.start(0);

  return offlineContext.startRendering();
}

/**
 * 读取并解码音频文件
 */
async function readAudioFile(file: File): Promise<AudioFileInfo> {
  try {
    const validation = validateFile(file);
    if (!validation.valid) {
      return { buffer: null, arrayBuffer: new ArrayBuffer(0), isValid: false, error: validation.error };
    }

    const originalArrayBuffer = await file.arrayBuffer();
    // 立即创建一个副本以避免后续的 detached buffer 问题
    const arrayBuffer = originalArrayBuffer.slice(0);
    const audioContext = await getAudioContext();

    // 为解码创建另一个副本
    const bufferForDecoding = arrayBuffer.slice(0);
    const buffer = await audioContext.decodeAudioData(bufferForDecoding);

    return { buffer, arrayBuffer, isValid: true };
  } catch (error) {
    return {
      buffer: null,
      arrayBuffer: new ArrayBuffer(0),
      isValid: false,
      error: `Failed to decode audio: ${error instanceof Error ? error.message : 'Unknown error'}`,
    };
  }
}

export async function concatenateWavFiles(file1: File, file2: File): Promise<Blob> {
  // 检查浏览器兼容性
  const browserSupport = checkBrowserSupport();
  if (!browserSupport.supported) {
    throw new Error(browserSupport.error);
  }

  let buffer1: AudioBuffer | null = null;
  let buffer2: AudioBuffer | null = null;
  let concatenatedBuffer: AudioBuffer | null = null;

  try {
    // 并行读取和解码两个音频文件
    const [audioInfo1, audioInfo2] = await Promise.all([readAudioFile(file1), readAudioFile(file2)]);

    // 验证文件是否成功读取
    if (!audioInfo1.isValid || !audioInfo1.buffer) {
      throw new Error(`File 1 error: ${audioInfo1.error}`);
    }

    if (!audioInfo2.isValid || !audioInfo2.buffer) {
      throw new Error(`File 2 error: ${audioInfo2.error}`);
    }

    buffer1 = audioInfo1.buffer;
    buffer2 = audioInfo2.buffer;

    const targetFormat: TargetAudioFormat = {
      sampleRate: buffer1.sampleRate,
      numberOfChannels: buffer1.numberOfChannels,
    };

    buffer2 = await normalizeAudioBuffer(buffer2, targetFormat);

    // 创建拼接后的音频缓冲区
    const totalLength = buffer1.length + buffer2.length;
    const audioContext = await getAudioContext();
    concatenatedBuffer = audioContext.createBuffer(buffer1.numberOfChannels, totalLength, buffer1.sampleRate);

    // 高效拷贝音频数据
    for (let channel = 0; channel < buffer1.numberOfChannels; channel++) {
      const outputChannel = concatenatedBuffer.getChannelData(channel);
      outputChannel.set(buffer1.getChannelData(channel), 0);
      outputChannel.set(buffer2.getChannelData(channel), buffer1.length);
    }

    // 输出统一转成 WAV；如果源文件本身是 WAV，则尽量保留其 metadata。
    return audioBufferToWav(concatenatedBuffer, audioInfo1.arrayBuffer);
  } catch (error) {
    throw new Error(`Concatenation failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

interface WavChunk {
  id: string;
  size: number;
  offset: number;
  data: ArrayBuffer;
}

/**
 * 优化的字符串写入函数
 */
function writeStringToView(view: DataView, offset: number, string: string): void {
  for (let i = 0; i < string.length; i++) {
    view.setUint8(offset + i, string.charCodeAt(i));
  }
}

function audioBufferToWav(buffer: AudioBuffer, originalFileBuffer?: ArrayBuffer): Blob {
  if (originalFileBuffer && isValidWavFile(originalFileBuffer)) {
    return audioBufferToWavWithMetadata(buffer, originalFileBuffer);
  }
  return audioBufferToBasicWav(buffer);
}

function audioBufferToBasicWav(buffer: AudioBuffer): Blob {
  const numberOfChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const dataSize = buffer.length * numberOfChannels * WAV_CONSTANTS.BYTES_PER_SAMPLE;
  const arrayBuffer = new ArrayBuffer(WAV_CONSTANTS.HEADER_SIZE + dataSize);
  const view = new DataView(arrayBuffer);
  let offset = 0;

  writeStringToView(view, offset, 'RIFF');
  offset += 4;
  view.setUint32(offset, arrayBuffer.byteLength - 8, true);
  offset += 4;
  writeStringToView(view, offset, 'WAVE');
  offset += 4;

  writeStringToView(view, offset, 'fmt ');
  offset += 4;
  view.setUint32(offset, 16, true);
  offset += 4;
  view.setUint16(offset, 1, true);
  offset += 2;
  view.setUint16(offset, numberOfChannels, true);
  offset += 2;
  view.setUint32(offset, sampleRate, true);
  offset += 4;

  const blockAlign = numberOfChannels * WAV_CONSTANTS.BYTES_PER_SAMPLE;
  view.setUint32(offset, sampleRate * blockAlign, true);
  offset += 4;
  view.setUint16(offset, blockAlign, true);
  offset += 2;
  view.setUint16(offset, WAV_CONSTANTS.BITS_PER_SAMPLE, true);
  offset += 2;

  writeStringToView(view, offset, 'data');
  offset += 4;
  view.setUint32(offset, dataSize, true);
  offset += 4;

  for (let i = 0; i < buffer.length; i++) {
    for (let channel = 0; channel < numberOfChannels; channel++) {
      const sample = Math.max(-1, Math.min(1, buffer.getChannelData(channel)[i]));
      view.setInt16(offset, sample * WAV_CONSTANTS.MAX_SAMPLE_VALUE, true);
      offset += WAV_CONSTANTS.BYTES_PER_SAMPLE;
    }
  }

  return new Blob([arrayBuffer], { type: 'audio/wav' });
}

// 修改后的WAV格式转换函数，保留原始元数据
function audioBufferToWavWithMetadata(buffer: AudioBuffer, originalFileBuffer: ArrayBuffer): Blob {
  const numberOfChannels = buffer.numberOfChannels;
  const sampleRate = buffer.sampleRate;
  const length = buffer.length * numberOfChannels * WAV_CONSTANTS.BYTES_PER_SAMPLE;

  try {
    // 解析原始WAV文件的chunks
    const chunks = parseWavChunks(originalFileBuffer);

    // 提取元数据chunks（排除RIFF、WAVE、data，保留fmt和其他所有chunks）
    const metadataChunks = chunks.filter((chunk) => chunk.id !== 'data' && chunk.id !== 'RIFF' && chunk.id !== 'WAVE');

    // 计算元数据总大小（包括fmt chunk）
    const metadataSize = metadataChunks.reduce(
      (total, chunk) => total + WAV_CONSTANTS.CHUNK_HEADER_SIZE + chunk.size + (chunk.size % 2),
      0,
    );

    // 计算新的文件大小：RIFF头(12) + 所有元数据chunks + data chunk头(8) + 音频数据
    const newDataSize = length;
    const newFileSize = WAV_CONSTANTS.RIFF_HEADER_SIZE + metadataSize + WAV_CONSTANTS.CHUNK_HEADER_SIZE + newDataSize;

    const arrayBuffer = new ArrayBuffer(newFileSize);
    const view = new DataView(arrayBuffer);
    let offset = 0;

    // RIFF头
    writeStringToView(view, offset, 'RIFF');
    offset += 4;
    view.setUint32(offset, newFileSize - 8, true); // 文件大小 - 8
    offset += 4;
    writeStringToView(view, offset, 'WAVE');
    offset += 4;

    // 复制所有元数据chunks（包括fmt chunk），但需要更新fmt chunk中的音频参数
    for (const chunk of metadataChunks) {
      writeStringToView(view, offset, chunk.id);
      offset += 4;
      view.setUint32(offset, chunk.size, true);
      offset += 4;

      if (chunk.id === 'fmt ') {
        // 对于fmt chunk，我们需要更新音频参数以匹配合并后的音频
        const fmtView = new DataView(chunk.data);
        const updatedFmt = new ArrayBuffer(chunk.size);
        const updatedFmtView = new DataView(updatedFmt);

        // 复制原始格式数据
        for (let i = 0; i < chunk.size; i++) {
          updatedFmtView.setUint8(i, fmtView.getUint8(i));
        }

        // 更新关键音频参数（保持其他扩展信息不变）
        if (chunk.size >= 16) {
          updatedFmtView.setUint16(2, numberOfChannels, true); // 声道数
          updatedFmtView.setUint32(4, sampleRate, true); // 采样率
          updatedFmtView.setUint32(8, sampleRate * numberOfChannels * WAV_CONSTANTS.BYTES_PER_SAMPLE, true); // 字节率
          updatedFmtView.setUint16(12, numberOfChannels * WAV_CONSTANTS.BYTES_PER_SAMPLE, true); // 块对齐
          updatedFmtView.setUint16(14, WAV_CONSTANTS.BITS_PER_SAMPLE, true); // 位深度
        }

        const updatedChunkData = new Uint8Array(updatedFmt);
        const targetView = new Uint8Array(arrayBuffer, offset, chunk.size);
        targetView.set(updatedChunkData);
      } else {
        // 其他chunks直接复制
        const chunkData = new Uint8Array(chunk.data);
        const targetView = new Uint8Array(arrayBuffer, offset, chunk.size);
        targetView.set(chunkData);
      }

      offset += chunk.size;

      // 如果chunk大小为奇数，需要填充一个字节以保持对齐
      if (chunk.size % 2 === 1) {
        view.setUint8(offset, 0);
        offset += 1;
      }
    }

    // data chunk
    writeStringToView(view, offset, 'data');
    offset += 4;
    view.setUint32(offset, newDataSize, true);
    offset += 4;

    // 写入音频数据
    for (let i = 0; i < buffer.length; i++) {
      for (let channel = 0; channel < numberOfChannels; channel++) {
        const sample = Math.max(-1, Math.min(1, buffer.getChannelData(channel)[i]));
        view.setInt16(offset, sample * WAV_CONSTANTS.MAX_SAMPLE_VALUE, true);
        offset += WAV_CONSTANTS.BYTES_PER_SAMPLE;
      }
    }

    return new Blob([arrayBuffer], { type: 'audio/wav' });
  } catch (error) {
    throw new Error(`WAV conversion failed: ${error instanceof Error ? error.message : 'Unknown error'}`);
  }
}

// 解析WAV文件的chunks
function parseWavChunks(buffer: ArrayBuffer): WavChunk[] {
  const view = new DataView(buffer);
  const chunks: WavChunk[] = [];

  // 跳过RIFF头（12字节）
  let offset = WAV_CONSTANTS.RIFF_HEADER_SIZE;

  while (offset < buffer.byteLength - WAV_CONSTANTS.CHUNK_HEADER_SIZE) {
    try {
      // 读取chunk ID（4字节）- 使用优化的读取方法
      const chunkId = readChunkId(view, offset);
      offset += 4;

      // 读取chunk大小（4字节）
      const chunkSize = view.getUint32(offset, true);
      offset += 4;

      // 边界检查
      if (offset + chunkSize > buffer.byteLength) {
        console.warn(`Chunk ${chunkId} size ${chunkSize} exceeds buffer boundary`);
        break;
      }

      // 提取chunk数据
      const chunkData = buffer.slice(offset, offset + chunkSize);

      chunks.push({
        id: chunkId,
        size: chunkSize,
        offset: offset - WAV_CONSTANTS.CHUNK_HEADER_SIZE,
        data: chunkData,
      });

      // 移动到下一个chunk（如果大小为奇数需要跳过填充字节）
      offset += chunkSize;
      if (chunkSize % 2 === 1) {
        offset += 1;
      }
    } catch (error) {
      console.error('Error parsing WAV chunk:', error);
      break;
    }
  }

  return chunks;
}

/**
 * 清理音频资源的工具函数
 */
export async function cleanup(): Promise<void> {
  await closeAudioContext();
}
